import { useFormikContext } from 'formik';
import { ChangeEvent, FC } from 'react';

import { FinanceQuote } from 'src/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { SliderWithTicks } from 'src/general/components/SliderWithTicks/SliderWithTicks';
import { formatNumber } from 'src/general/helpers/formatNumber';
import { getMicrocopy } from 'src/stores/microcopyStore';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { FinanceQuotesFormValues } from '../../templates/Checkout/Forms/CheckoutForm.types';
import styles from './FinanceQuoteTermSlider.module.scss';

interface Props {
  fieldName: keyof FinanceQuotesFormValues;
  quotes: FinanceQuote[];
  stepInterval?: number;
}

export const FinanceQuoteTermSlider: FC<Props> = ({ fieldName, quotes, stepInterval }) => {
  const { values, setFieldValue } = useFormikContext<FinanceQuotesFormValues>();

  const marks: number[] = [...new Set(quotes.map((quote) => quote.offer.term_months))]
    .filter((term) => term !== 0) // Dummy quotes / blanket quote refusals have terms of 0 months
    .sort((a, b) => a - b);

  const quotesAvailable = marks.length !== 0;
  const minValue = quotesAvailable ? marks[0] : undefined;
  const maxValue = quotesAvailable ? marks[marks.length - 1] : undefined;

  const title = getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_LOAN_TERM_HEADER, false, {
    term: monthsToYears(values[fieldName] as number),
  });
  const noQuoteTitle = getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_LOAN_TERM_NO_QUOTE_AVAILABLE_HEADER);
  const description = getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_LOAN_TERM_DESCRIPTION, false, {
    min: monthsToYears(minValue ?? 0),
    max: monthsToYears(maxValue ?? 0),
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFieldValue(fieldName, Number(e.target.value));
    pushToDataLayer({
      event: `checkout_quote-slider`,
      value: e.target.value,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title} data-testid="finance-quote-term-slider-title">
          {quotesAvailable ? title : noQuoteTitle}
        </div>
        <div className={styles.description} data-testid="finance-quote-term-slider-description">
          {quotesAvailable ? description : <>&nbsp;</>}
        </div>
      </div>
      <SliderWithTicks
        value={values[fieldName] as number}
        min={minValue}
        max={maxValue}
        step={stepInterval ?? 1}
        onChange={onChange}
        disabled={!quotesAvailable}
        ariaLabel="Interest rate"
        marks={marks}
      />
    </div>
  );
};

const monthsToYears = (months: number): string => formatNumber(months / 12, { decimals: 1, trimZeroDecimals: true });
